import { useState } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IconTypes } from "../../components/icons.types";
import {
  OUTPUT_FORK_KB,
  OutputForkAnswer,
  answerOutputFork,
  trackOutputForkNext,
} from "../../features/catalogs/outputFork";

const SURFACE = "publish";

type Props = {
  /** The live catalog link, the one Copy link puts on the clipboard. */
  link: string;
  /** Called once with the answer the user picked; the dialog logs "dismissed" if none was. */
  onAnswered: () => void;
  onDone: () => void;
};

/**
 * S117 step 1, surface A: what the publish dialog turns into after an account's first publish.
 * Text is Andriy's exact-text OK of 10-28 (memory/sessions/117-send-to-buyers-v1.md); no new words.
 */
export const OutputForkPublish = ({ link, onAnswered, onDone }: Props) => {
  const navigate = useNavigate();
  const [answer, setAnswer] = useState<Exclude<OutputForkAnswer, "dismissed">>();
  const [copied, setCopied] = useState(false);

  const pick = (a: Exclude<OutputForkAnswer, "dismissed">) => {
    setAnswer(a);
    answerOutputFork(SURFACE, a);
    onAnswered();
  };

  const copyLink = async () => {
    trackOutputForkNext(SURFACE, "copy-link");
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard blocked: the link is still in the Share dialog.
    }
  };

  const copyButton = (
    <Button
      variant="contained"
      onClick={copyLink}
      startIcon={copied ? <IconTypes.Check /> : <IconTypes.CopyAction />}
    >
      Copy link
    </Button>
  );

  const openKb = (url: string, next: string) => {
    trackOutputForkNext(SURFACE, next);
    window.open(url, "_blank");
  };

  if (!answer) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Who is it for?
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button variant="contained" onClick={() => pick("buyers")}>
            Buyers I sell to again
          </Button>
          <Button variant="outlined" onClick={() => pick("one_off")}>
            Just this once
          </Button>
        </Stack>
      </Box>
    );
  }

  if (answer === "one_off") {
    return (
      <Box sx={{ py: 1 }}>
        <Typography sx={{ mb: 2 }}>
          All set. Your link stays live as long as you need it, and the PDF is
          in Share.
        </Typography>
        <Stack direction="row" spacing={2}>
          {copyButton}
          <Button onClick={onDone}>Done</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography sx={{ mb: 2 }}>
        Send buyers the link. It always shows your latest products and prices,
        and you can see when they open it.
      </Typography>
      <Box sx={{ mb: 3 }}>{copyButton}</Box>
      <Typography sx={{ mb: 1 }}>
        <b>Three things buyers use:</b>
      </Typography>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography>
          Price list: a page with the code, name and price of every product.{" "}
          <Link
            component="button"
            onClick={() => openKb(OUTPUT_FORK_KB.lineSheet, "line-sheet")}
          >
            How to add one
          </Link>
        </Typography>
        <Typography>
          Showroom: buyers browse your catalogs and send you an order.{" "}
          <Link
            component="button"
            onClick={() => {
              trackOutputForkNext(SURFACE, "showroom");
              onDone();
              navigate("/showrooms");
            }}
          >
            Set it up
          </Link>
        </Typography>
        <Typography>
          Different prices for different buyers.{" "}
          <Link
            component="button"
            onClick={() => openKb(OUTPUT_FORK_KB.buyerPrices, "buyer-prices")}
          >
            How it works
          </Link>
        </Typography>
      </Stack>
      <Button variant="outlined" onClick={onDone}>
        Done
      </Button>
    </Box>
  );
};
